<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-device-asset-statistics">
    <nx-report-header title="设备资产统计分析">
      <div class="kb-header-actions">
        <span style="font-size: 12px; color: #64748b">数据刷新 {{ data.refreshTime || '—' }}</span>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-head-note">设备类型占比、区域分布、采购趋势与运行状态占比一览。</div>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="设备分类">
        <el-input v-model="category" clearable placeholder="全部或输入分类" style="width: 160px" />
      </el-form-item>
      <el-form-item label="采购统计粒度">
        <el-select v-model="purchaseGrain" style="width: 120px" @change="loadData">
          <el-option label="按年" value="year" />
          <el-option label="按季度" value="quarter" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadData">查询</el-button>
      </el-form-item>
    </el-form>

    <div class="kb-section">
      <div class="kb-section-title">设备类型统计分析</div>
      <div class="kb-section-sub">各类型设备数量占比，呈现园区设备结构组成。</div>
      <div class="kb-grid-2">
        <div ref="chartTypeRef" class="kb-chart" />
        <div class="kb-table-wrap kb-table-equal" style="--kb-cols: 3">
          <el-table :data="data.deviceTypeStatistics || []" border stripe size="small" max-height="300">
            <el-table-column label="类型" align="center">
              <template slot-scope="{ row }">{{ deviceTypeLabel(row.device_type) }}</template>
            </el-table-column>
            <el-table-column prop="device_count" label="台数" align="right" />
            <el-table-column label="占比" align="right">
              <template slot-scope="{ row }">{{ fmtNum(row.percentage) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-title">区域分布统计分析</div>
      <div class="kb-section-sub">各区域按设备类型的台数明细（柱状图为区域合计）。</div>
      <div class="kb-grid-2">
        <div ref="chartAreaRef" class="kb-chart" />
        <div
          class="kb-table-wrap kb-table-wrap--multiline kb-table-equal"
          style="--kb-cols: 3"
        >
          <el-table :data="data.areaByTypeDetail || []" border stripe size="small" max-height="300">
            <el-table-column prop="area_name" label="区域" align="center" />
            <el-table-column label="类型" align="center">
              <template slot-scope="{ row }">{{ deviceTypeLabel(row.device_type) }}</template>
            </el-table-column>
            <el-table-column prop="device_count" label="台数" align="right" />
          </el-table>
        </div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-title">采购时间统计分析</div>
      <div class="kb-section-sub">按{{ purchaseGrain === 'quarter' ? '季度' : '年度' }}汇总采购台数。</div>
      <div class="kb-grid-2">
        <div ref="chartPurchaseRef" class="kb-chart" />
        <div
          class="kb-table-wrap kb-table-equal"
          :style="{ '--kb-cols': purchaseGrain === 'quarter' ? 4 : 3 }"
        >
          <el-table :data="data.purchaseTrend || []" border stripe size="small" max-height="300">
            <el-table-column v-if="purchaseGrain === 'quarter'" prop="year" label="年" align="center" />
            <el-table-column v-if="purchaseGrain === 'quarter'" prop="quarter" label="季" align="center" />
            <el-table-column v-else prop="year" label="年" align="center" />
            <el-table-column label="类型" align="center">
              <template slot-scope="{ row }">{{ deviceTypeLabel(row.device_type) }}</template>
            </el-table-column>
            <el-table-column prop="device_count" label="采购台数" align="right" />
          </el-table>
        </div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-title">设备状态占比分析</div>
      <div class="kb-section-sub">正常运行、维修、故障、停用、报废等状态分布。</div>
      <div class="kb-grid-2">
        <div ref="chartStatusRef" class="kb-chart" />
        <div class="kb-table-wrap kb-table-equal" style="--kb-cols: 3">
          <el-table :data="data.operationStatusStatistics || []" border stripe size="small" max-height="300">
            <el-table-column label="状态" align="center">
              <template slot-scope="{ row }">{{ opStatusLabel(row.operation_status) }}</template>
            </el-table-column>
            <el-table-column prop="device_count" label="台数" align="right" />
            <el-table-column label="占比" align="right">
              <template slot-scope="{ row }">{{ fmtNum(row.percentage) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { getDictionary } from '@/api/system/dictbiz'
import { fetchDeviceAssetStatistics } from './api'
import './index.scss'

const OP_STATUS = {
  0: '正常',
  1: '故障',
  2: '维修',
  3: '报废',
  4: '停用',
  5: '其他'
}

export default {
  name: 'KanbanDeviceAssetStatistics',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      category: '',
      purchaseGrain: 'year',
      data: {},
      deviceTypeDictMap: {},
      chartType: null,
      chartArea: null,
      chartPurchase: null,
      chartStatus: null
    }
  },
  created() {
    getDictionary({ code: 'base_device_type' })
      .then((res) => {
        const list = res && res.data && res.data.data
        if (!Array.isArray(list)) return
        const m = {}
        list.forEach((item) => {
          if (item && item.dictKey != null) {
            m[String(item.dictKey)] = item.dictValue || String(item.dictKey)
          }
        })
        this.deviceTypeDictMap = m
        this.$nextTick(() => {
          if (this.data && Object.keys(this.data).length) this.renderCharts()
        })
      })
      .catch(() => {})
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    ;[this.chartType, this.chartArea, this.chartPurchase, this.chartStatus].forEach((c) => {
      if (c) c.dispose()
    })
  },
  methods: {
    deviceTypeLabel(code) {
      if (code === null || code === undefined || code === '') return '—'
      const k = String(code)
      const v = this.deviceTypeDictMap[k]
      if (v) return v
      return k
    },
    fmtNum(v) {
      if (v == null || v === '') return '—'
      const n = typeof v === 'number' ? v : parseFloat(v)
      return Number.isNaN(n) ? String(v) : `${n.toFixed(2)}%`
    },
    opStatusLabel(v) {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      if (!Number.isNaN(n) && OP_STATUS[n] != null) return OP_STATUS[n]
      return '—'
    },
    onResize() {
      [this.chartType, this.chartArea, this.chartPurchase, this.chartStatus].forEach((c) => c && c.resize())
    },
    async loadData() {
      this.loading = true
      try {
        const res = await fetchDeviceAssetStatistics({
          category: this.category || undefined,
          purchaseGrain: this.purchaseGrain
        })
        this.data = res || {}
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderCharts() {
      const d = this.data

      const elType = this.$refs.chartTypeRef
      if (elType) {
        const rows = d.deviceTypeStatistics || []
        if (!this.chartType) this.chartType = echarts.init(elType)
        this.chartType.setOption({
          title: { text: '类型占比', left: 'center', top: 0, textStyle: { fontSize: 13 } },
          tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
          legend: { bottom: 0, type: 'scroll' },
          series: [
            {
              type: 'pie',
              radius: ['36%', '58%'],
              data: rows.map((r) => ({
                name: this.deviceTypeLabel(r.device_type),
                value: Number(r.device_count) || 0
              }))
            }
          ]
        })
      }

      const elArea = this.$refs.chartAreaRef
      if (elArea) {
        const rows = d.areaCountByArea || []
        if (!this.chartArea) this.chartArea = echarts.init(elArea)
        this.chartArea.setOption({
          title: { text: '区域台数分布', left: 'center', top: 0, textStyle: { fontSize: 13 } },
          tooltip: { trigger: 'axis' },
          grid: { left: 48, right: 16, top: 36, bottom: 64 },
          xAxis: {
            type: 'category',
            data: rows.map((r) => r.area_name || '—'),
            axisLabel: { rotate: 30 }
          },
          yAxis: { type: 'value', name: '台数' },
          series: [{ type: 'bar', data: rows.map((r) => Number(r.device_count) || 0), itemStyle: { color: '#2563eb' } }]
        })
      }

      const elPur = this.$refs.chartPurchaseRef
      if (elPur) {
        const rows = d.purchaseTrendByPeriod || []
        if (!this.chartPurchase) this.chartPurchase = echarts.init(elPur)
        this.chartPurchase.setOption({
          title: {
            text: this.purchaseGrain === 'quarter' ? '采购台数（季度）' : '采购台数（年度）',
            left: 'center',
            top: 0,
            textStyle: { fontSize: 13 }
          },
          tooltip: { trigger: 'axis' },
          grid: { left: 48, right: 16, top: 36, bottom: 32 },
          xAxis: { type: 'category', data: rows.map((r) => r.period) },
          yAxis: { type: 'value', name: '台数' },
          series: [
            {
              type: 'bar',
              data: rows.map((r) => Number(r.device_count) || 0),
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#38bdf8' },
                  { offset: 1, color: '#2563eb' }
                ])
              }
            }
          ]
        })
      }

      const elSt = this.$refs.chartStatusRef
      if (elSt) {
        const rows = d.operationStatusStatistics || []
        if (!this.chartStatus) this.chartStatus = echarts.init(elSt)
        this.chartStatus.setOption({
          title: { text: '运行状态占比', left: 'center', top: 0, textStyle: { fontSize: 13 } },
          tooltip: { trigger: 'item', formatter: '{b}: {c}台 ({d}%)' },
          legend: { bottom: 0, type: 'scroll' },
          series: [
            {
              type: 'pie',
              radius: ['32%', '56%'],
              data: rows.map((r) => ({
                name: this.opStatusLabel(r.operation_status),
                value: Number(r.device_count) || 0
              }))
            }
          ]
        })
      }
    }
  }
}
</script>
